/**
 * Live-camera capture seam for the device "Location & photos" section.
 *
 * Owner request 2026-09-15: an installer with a tablet/phone should be able to take a LIVE
 * picture in-app, REVIEW it, and Use or Retake before it uploads — rather than the OS picker
 * firing an immediate upload. The React modal (DeviceCameraCapture.tsx) drives getUserMedia,
 * the <video> preview and the review step; THIS module holds every piece of that flow that can
 * be reasoned about without a live camera, so it runs under `node --test` (deviceCamera.test.ts).
 *
 * getUserMedia, MediaStream and canvas.toBlob do not exist in the Node test env, so the
 * DOM-touching helpers below take deliberately NARROW structural interfaces (BlobCanvas,
 * DrawTarget, StreamLike): a real HTMLCanvasElement / MediaStream satisfies them, and so does a
 * hand-rolled stand-in in the tests. That is the "thin seam" — the component wires the real DOM
 * objects in; the tests wire fakes in; the logic in between is identical and verified.
 *
 * The captured still re-uses the EXISTING upload path unchanged: the modal wraps the JPEG blob
 * from here in a File and feeds it to the same downscale + EXIF-strip + upload routine a chosen
 * file goes through (devicePhotoPrep.prepareForUpload). Nothing here talks to the network.
 */

/** JPEG quality for the captured still — matches devicePhotoPrep's re-encode (0.85). */
export const CAMERA_JPEG_QUALITY = 0.85;

/* ------------------------------------------------------------------------------------------ *
 * Availability + pure decisions (no DOM at all)
 * ------------------------------------------------------------------------------------------ */

/** The slice of `navigator` this feature probes. Optional throughout so tests pass partials. */
export interface CameraEnv {
  mediaDevices?: {
    getUserMedia?: unknown;
    enumerateDevices?: unknown;
  };
  /** window.isSecureContext — getUserMedia is blocked on insecure origins. */
  isSecureContext?: boolean;
}

/**
 * True when this browser can even attempt a live capture: getUserMedia present AND a secure
 * context (https / localhost). When false the UI never offers "Take photo" and falls straight
 * back to the file input — no dead end. (Meridian is served over HTTPS at cloud.gtkcentral.net,
 * so this is expected to be true in the field; it guards older browsers and http previews.)
 */
export function cameraCaptureSupported(env: CameraEnv | undefined | null): boolean {
  if (!env) return false;
  // `isSecureContext` is only a blocker when explicitly false; undefined (old browser) is
  // treated as "unknown, let getUserMedia decide" so we do not hide the option needlessly.
  const secure = env.isSecureContext !== false;
  const md = env.mediaDevices;
  return secure && typeof md?.getUserMedia === "function";
}

/**
 * True when a free photo slot exists for a writer who is not mid-upload — the gate for showing
 * (and enabling) "Take photo". Mirrors the file-input gating in DeviceLocationPhotos so the two
 * entry points appear and disappear together.
 */
export function canTakeLivePhoto(
  count: number,
  max: number,
  canWrite: boolean,
  busy: boolean,
): boolean {
  return canWrite && !busy && count < max;
}

/** MediaDeviceInfo is `{ kind }`-shaped for our purposes; keep the test stand-ins tiny. */
export interface DeviceKindLike {
  kind: string;
}

/** True when more than one camera exists, so the front/back switch is worth showing. */
export function hasMultipleCameras(devices: readonly DeviceKindLike[] | null | undefined): boolean {
  if (!devices) return false;
  return devices.filter((d) => d.kind === "videoinput").length > 1;
}

/** Camera facings we toggle between. "environment" (rear) is the install-photo default. */
export type FacingMode = "environment" | "user";

/** Flip rear <-> front for the switch-camera button. */
export function nextFacingMode(mode: FacingMode): FacingMode {
  return mode === "environment" ? "user" : "environment";
}

/**
 * Deterministic, ASCII-only filename for a captured still: `camera-YYYYMMDD-HHMMSS.jpg`.
 * The stem carries no path/colon so it is safe as a multipart filename, and the .jpg matches
 * the canvas re-encode. `now` is injectable so the test does not depend on the wall clock.
 */
export function frameFileName(now: Date = new Date()): string {
  const p = (n: number, width = 2) => String(n).padStart(width, "0");
  const stamp =
    `${p(now.getFullYear(), 4)}${p(now.getMonth() + 1)}${p(now.getDate())}` +
    `-${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
  return `camera-${stamp}.jpg`;
}

/**
 * The plain sentence + fallback decision for a getUserMedia failure. `name` is the DOMException
 * name (e.g. "NotAllowedError"). Every branch offers the file input as a way forward, so the
 * installer is never stuck — the message only explains why the live camera did not open.
 */
export function cameraErrorMessage(name: string | undefined): {
  message: string;
  offerFileFallback: boolean;
} {
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return {
        message: "Camera access was blocked. Allow the camera in your browser, or choose a file instead.",
        offerFileFallback: true,
      };
    case "NotFoundError":
    case "OverconstrainedError":
    case "DevicesNotFoundError":
      return {
        message: "No camera was found on this device. You can choose a file instead.",
        offerFileFallback: true,
      };
    case "NotReadableError":
    case "TrackStartError":
      return {
        message: "The camera is in use by another app. Close it and try again, or choose a file instead.",
        offerFileFallback: true,
      };
    default:
      return {
        message: "The camera could not be started. You can choose a file instead.",
        offerFileFallback: true,
      };
  }
}

/* ------------------------------------------------------------------------------------------ *
 * Thin DOM seam (narrow interfaces; real DOM objects and test fakes both satisfy them)
 * ------------------------------------------------------------------------------------------ */

/** A stoppable media track — the subset of MediaStreamTrack we touch. */
export interface TrackLike {
  stop(): void;
}

/** The subset of MediaStream we touch (a real MediaStream satisfies this). */
export interface StreamLike {
  getTracks(): TrackLike[];
}

/**
 * Stop every track on a stream so the OS camera indicator goes off. Safe to call with null and
 * safe to call twice (a track already stopped ignores a second stop). This is the single choke
 * point every lifecycle exit funnels through — shutter, retake, close and unmount — so "no stuck
 * camera light" is enforced in ONE place instead of four.
 */
export function stopMediaStream(stream: StreamLike | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

/** The size we read off the live <video> (its NATIVE resolution, not the CSS box). */
export interface FrameVideo {
  videoWidth: number;
  videoHeight: number;
}

/** The 2D context subset we draw through. */
export interface DrawContext {
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}

/** The canvas subset we draw into — a real HTMLCanvasElement satisfies this. */
export interface DrawTarget {
  width: number;
  height: number;
  getContext(contextId: "2d"): DrawContext | null;
}

/**
 * Draw the current video frame onto `canvas` at the video's NATIVE resolution (so a 12 MP
 * tablet sensor is captured full-res; the existing prepareForUpload path then downscales to
 * 2560 px, exactly as it does for a chosen file). Throws if no 2D context is available.
 */
export function drawFrameToCanvas(video: FrameVideo, canvas: DrawTarget): void {
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("A 2D canvas context is not available for capture.");
  // Cast: FrameVideo is only the size-bearing subset; the real argument is an HTMLVideoElement,
  // which IS a CanvasImageSource. Tests pass a stand-in and assert the call, not the pixels.
  ctx.drawImage(video as unknown as CanvasImageSource, 0, 0, canvas.width, canvas.height);
}

/** The canvas subset that can hand back a Blob — a real HTMLCanvasElement satisfies this. */
export interface BlobCanvas {
  toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

/**
 * Wrap canvas.toBlob as a JPEG Promise. Rejects when the browser hands back null (out of memory
 * / tainted canvas) so the caller can surface a plain error rather than uploading nothing.
 */
export function canvasToJpegBlob(
  canvas: BlobCanvas,
  quality: number = CAMERA_JPEG_QUALITY,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The photo could not be captured. Try again."))),
      "image/jpeg",
      quality,
    );
  });
}
